// VIDEO-EDITING — SSE pipeline state. Subscribes to the central
// appEvents bus so progress + result + error variants flow into the
// per-project SseState without each consuming component re-subscribing.
import type { StoreApi } from "zustand";

import { appEvents } from "@/shared/lib/events";

import type { SseState, VideoEditorState } from "../types";
import { EMPTY_SSE } from "../utils";

type Set = StoreApi<VideoEditorState>["setState"];

export type SseActions = Pick<VideoEditorState, "setSse">;

export function createSseActions(set: Set): SseActions {
  return {
    setSse: (projectId, patch) => {
      set((state) => {
        const existing: SseState = state.sse[projectId] ?? EMPTY_SSE;
        return {
          ...state,
          sse: { ...state.sse, [projectId]: { ...existing, ...patch } },
        };
      });
    },
  };
}

// Wire the appEvents bus → store. Returns the unsubscribe handle; the
// store calls this at module-init so the slice keeps the per-project
// progress + error fields in sync across re-mounts.
export function wireVideoEditorSse(
  api: StoreApi<VideoEditorState>,
): () => void {
  const update = (
    projectId: string,
    patch: Partial<SseState>,
  ): void => {
    const state = api.getState();
    const existing: SseState = state.sse[projectId] ?? EMPTY_SSE;
    api.setState({
      ...state,
      sse: { ...state.sse, [projectId]: { ...existing, ...patch } },
    });
  };

  const offs = [
    appEvents.subscribe("video-editor:translate-progress", (e) => {
      update(e.projectId, {
        translating: e.targetLanguage,
        translateProgress: { stage: e.stage, pct: e.pct, message: e.message },
      });
    }),
    appEvents.subscribe("video-editor:translate-result", (e) => {
      update(e.projectId, {
        translating: null,
        translateProgress: null,
        translateError: null,
      });
    }),
    appEvents.subscribe("video-editor:translate-error", (e) => {
      update(e.projectId, {
        translating: null,
        translateProgress: null,
        translateError: e.message,
      });
    }),
    appEvents.subscribe("video-editor:export-progress", (e) => {
      update(e.projectId, {
        exporting: e.format,
        exportProgress: { stage: e.stage, pct: e.pct, message: e.message },
      });
    }),
    appEvents.subscribe("video-editor:export-result", (e) => {
      update(e.projectId, {
        exporting: null,
        exportProgress: null,
        exportError: null,
      });
    }),
    appEvents.subscribe("video-editor:export-error", (e) => {
      update(e.projectId, {
        exporting: null,
        exportProgress: null,
        exportError: e.message,
      });
    }),
    appEvents.subscribe("video-editor:transcribe-progress", (e) => {
      update(e.projectId, {
        transcribing: true,
        transcribeProgress: {
          stage: e.stage,
          pct: e.pct,
          message: e.message,
        },
      });
    }),
    appEvents.subscribe("video-editor:transcribe-result", (e) => {
      update(e.projectId, {
        transcribing: false,
        transcribeProgress: null,
        transcribeError: null,
      });
    }),
    appEvents.subscribe("video-editor:transcribe-error", (e) => {
      update(e.projectId, {
        transcribing: false,
        transcribeProgress: null,
        transcribeError: e.message,
      });
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
